<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <nav
    v-if="paginationType === 'pagination'"
    :aria-label="$string('general.pagination.label')"
    class="pagination liquid"
    :class="{ [$class('loading')]: paginationIsLoading }"
  >
    <ul class="pagination__list">
      <li
        class="pagination__list-item"
        :class="{ [$class('hidden')]: !pagination.hasPreviousPage }"
      >
        <a
          class="pagination__button pagination__part"
          :tabindex="pagination.hasPreviousPage ? 0 : -1"
          :href="pageUrl(currentPage - 1)"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.pagination.previous')"
          />
          <icon-left />
        </a>
      </li>

      <li class="pagination__list-item">
        <a
          class="pagination__link pagination__part text-body-m-regular"
          :class="{ [$class('active')]: isCurrentPage(currentPage, 1) }"
          :href="pageUrl(1)"
          :aria-current="isCurrentPage(currentPage, 1) ? 'page' : null"
        >
          {{ 1 }}
        </a>
      </li>

      <li
        v-if="currentPage > Math.ceil(offset / 2) + 1"
        class="text-body-m-regular"
        v-text="'&hellip;'"
      />

      <li
        v-for="index of pagesWithOffset"
        :key="index"
        class="pagination__list-item"
      >
        <a
          class="pagination__link pagination__part text-body-m-regular"
          :class="{ [$class('active')]: isCurrentPage(currentPage, index) }"
          :href="pageUrl(index)"
          :aria-current="isCurrentPage(currentPage, index) ? 'page' : null"
        >
          {{ index }}
        </a>
      </li>

      <li
        v-if="currentPage < pagination.pages - Math.floor(offset / 2) - 1"
        class="text-body-m-regular"
        v-text="'&hellip;'"
      />

      <li class="pagination__list-item">
        <a
          class="pagination__link pagination__part text-body-m-regular"
          :class="{ [$class('active')]: isCurrentPage(currentPage, pagination.pages) }"
          :href="pageUrl(pagination.pages)"
          :aria-current="isCurrentPage(currentPage, pagination.pages) ? 'page' : null"
        >
          {{ pagination.pages }}
        </a>
      </li>

      <li
        class="pagination__list-item"
        :class="{ [$class('hidden')]: currentPage >= pagination.pages }"
      >
        <a
          class="pagination__button pagination__part"
          :tabindex="pagination.hasNextPage ? 0 : -1"
          :href="pageUrl(currentPage + 1)"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.pagination.next')"
          />
          <icon-right />
        </a>
      </li>
    </ul>
  </nav>
  <!-- Load More + View All -->
  <div
    v-if="paginationType === 'load_more'"
    class="pagination__load-more-container"
  >
    <p class="pagination__load-more-count text-body-m-regular">
      {{ clampedCurrentProductsCount }} / {{ totalProductsCount }}
    </p>

    <div
      v-if="showPaginationButtons"
      class="pagination__load-more-actions"
    >
      <btn
        class="pagination__load-more-btn"
        :label="$string('general.pagination.more_products')"
        modifiers="secondary"
        type="button"
        :disabled="clampedCurrentProductsCount >= totalProductsCount"
        @click="$emit('load-more')"
      />
      <btn
        class="pagination__load-more-btn"
        :label="$string('general.pagination.see_all')"
        modifiers="secondary"
        type="button"
        @click="$emit('view-all')"
      />
    </div>
  </div>

  <!-- Progress Bar + Load More -->
  <div
    v-if="paginationType === 'progress_bar'"
    class="pagination__progress-bar-container"
  >
    <p class="pagination__progress-bar-count text-body-m-regular">
      {{ clampedCurrentProductsCount }} / {{ totalProductsCount }}
    </p>
    <div class="pagination__progress-bar">
      <div
        class="pagination__progress-bar-fill"
        :style="{ width: fillPercentage }"
      />
    </div>
    <btn
      class="button button--secondary pagination__progress-bar-btn"
      :label="$string('general.pagination.see_more')"
      modifiers="secondary"
      type="button"
      @click="$emit('load-more')"
    />
  </div>
</template>

<script>

/**
 * Vue: Main pagination (main-pagination)
 * -----------------------------------------------------------------------------
 * Main pagination used for the main collection
 *
 * @param {String} currentPage - The current page of the pagination.
 * @param {Object} pagination - Pagination object.
 *
 */
import IconLeft from '~icons/directional-navigation/chevron/left.svg'
import IconRight from '~icons/directional-navigation/chevron/right.svg'
import Btn from '~global/btn/btn'

export default {
  name: 'MainPagination',

  components: {
    IconLeft,
    IconRight,
    Btn,
  },

  props: {
    currentPage: {
      type: Number,
      default: 1,
    },
    pagination: {
      type: Object,
      default: () => ({}),
    },
    paginationType: {
      type: String,
      default: 'default',
    },
    currentProductsCount: {
      type: Number,
      default: 0,
    },
    totalProductsCount: {
      type: Number,
      default: 0,
    },
    showPaginationButtons: {
      type: Boolean,
      default: true,
    },

    paginationIsLoading: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'load-more': () => true,
    'view-all': () => true,
  },

  data() {
    return {
      offset: 3,
      pagesWithOffset: [],
    }
  },

  computed: {

    /**
     * Get the clamped current products count.
     * @returns {Number}
     */
    clampedCurrentProductsCount() {
      return Math.min(this.currentProductsCount, this.totalProductsCount)
    },

    /**
     * Calculate the fill percentage of the progress bar.
     * @returns {String}
     */
    fillPercentage() {
      if (this.totalProductsCount <= 0) {
        return '0%'
      }

      const fraction = this.currentProductsCount / this.totalProductsCount
      const clamped = Math.min(Math.max(fraction, 0), 1)

      return `${(clamped * 100).toFixed(2)}%`
    },
  },

  watch: {
    currentPage() {
      this.buildPageOffsetArray()
    },

    /**
     * Watch pagination object to update offset array accordingly.
     */
    pagination() {
      this.buildPageOffsetArray()
    },
  },

  mounted() {
    this.buildPageOffsetArray()
  },

  methods: {

    /**
     * Build the array of pages to display in between the first and last page.
     * - Will be rendered in between the ellipses, if they exist.
     * - Modified from Childsplay Clothing's Boost Pagination component.
     */
    buildPageOffsetArray() {
      // Make an array of pages without first and last page link.
      const pageArray = [...Array(this.pagination.pages).keys()]

      pageArray.shift()
      pageArray.shift()

      const halfOffset = Math.ceil(this.offset / 2)
      let offset = this.offset - 1
      let start = 0

      if (this.currentPage > halfOffset) {
        offset = this.offset
        start = this.currentPage - halfOffset - 1
      }

      if (this.currentPage > this.pagination.pages - halfOffset) {
        start = this.pagination.pages - this.offset - 1
      }

      this.pagesWithOffset = pageArray.splice(start, offset)
    },

    /**
     * Determine if a page is the active page.
     * @param {Number} page
     * @param {Number} target
     *
     * @returns {Boolean}
     */
    isCurrentPage(page, target) {
      return page === target
    },

    /**
     * Returns the page URL.
     * @param {Number} page - target page number.
     *
     * @returns {String}
     */
    pageUrl(page) {
      const params = new URLSearchParams(window.location.search)
      params.set('page', page)

      return `${window.location.pathname}?${params.toString()}`
    },
  },
}
</script>

<style lang="scss">
@import '@/components/pagination';
</style>
